/* Mobile "Install app" prompt.

   On Chrome/Android we capture the native `beforeinstallprompt` event so the
   banner's Install button can trigger the real OS dialog. On iOS Safari the
   event never fires, so we instead show a short "Share → Add to Home Screen"
   tip.

   Ground rules so this stays unobtrusive:
   - Only on mobile-sized viewports (< 900px — same breakpoint the rest of the
     app uses to switch to the fixed bottom nav).
   - Never when the app is already running standalone (user has already
     installed it).
   - SKIP on first visit. First-time visitors see a clean page; the install
     banner only surfaces from the SECOND cold load onward. Reasoning:
     someone landing for the first time is still evaluating whether they
     even like the app — interrupting that with an install ask is the
     fastest way to a dismissal + 1-day cooldown.
   - 30s delay before surfacing on eligible visits — gives the user enough
     time to actually see content before being asked to commit to an install.
   - A 1-day cooldown after dismissal so we don't nag on every visit. */

const DISMISS_KEY     = "sv:install-dismissed-at";
const VISIT_COUNT_KEY = "sv:install-visit-count";
const COOLDOWN_MS = 1 * 24 * 60 * 60 * 1000; // 1 day
const MOBILE_MAX_WIDTH = 899;
const INSTALL_DELAY_MS = 30 * 1000;          // 30 seconds before showing
const MIN_VISITS_BEFORE_SHOW = 2;            // skip first visit entirely

let deferredPrompt = null;
let bannerEl = null;
let initialised = false;
let pendingShow = false; // beforeinstallprompt fired before init() ran
let visitsThisInit = 0;  // snapshot of visit count taken once per init

/* CRITICAL: register the beforeinstallprompt listener at module-import time.
   Chrome fires this event very early in page load (often before Firebase auth
   resolves), so if we waited until initInstallPrompt() ran we'd miss it on
   most loads. We capture the event eagerly and defer the actual banner show
   until init() has decided we're allowed to nag the user. */
window.addEventListener("beforeinstallprompt", (e) => {
  e.preventDefault();
  deferredPrompt = e;
  console.info("[install] beforeinstallprompt captured");
  if (initialised) {
    if (!recentlyDismissed() && isMobileViewport() && !isStandalone()
        && visitsThisInit >= MIN_VISITS_BEFORE_SHOW) {
      // 30s delay so the user sees the app before being prompted to install.
      scheduleBanner(androidBannerHtml(), INSTALL_DELAY_MS);
    }
  } else {
    pendingShow = true;
  }
});

window.addEventListener("appinstalled", () => {
  console.info("[install] appinstalled, hiding banner");
  deferredPrompt = null;
  markDismissed();
  hideBanner();
});

function isStandalone() {
  return (
    (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
    window.navigator.standalone === true // iOS Safari's non-standard flag
  );
}

function isMobileViewport() {
  return window.innerWidth <= MOBILE_MAX_WIDTH;
}

function isIosSafari() {
  const ua = navigator.userAgent || "";
  const isIos = /iPad|iPhone|iPod/i.test(ua) && !window.MSStream;
  // Safari check: exclude Chrome / Edge / Firefox / Android webviews.
  const isSafari = /^((?!chrome|android|crios|fxios|edgios|opr).)*safari/i.test(ua);
  return isIos && isSafari;
}

function recentlyDismissed() {
  try {
    const at = Number(localStorage.getItem(DISMISS_KEY)) || 0;
    return Date.now() - at < COOLDOWN_MS;
  } catch {
    return false;
  }
}

function markDismissed() {
  try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
}

/** Increment the visit-count counter and return the post-increment value.
 *  Called once per cold start from initInstallPrompt. The very first
 *  call returns 1, the second returns 2, and so on. */
function bumpVisitCount() {
  try {
    const current = Number(localStorage.getItem(VISIT_COUNT_KEY)) || 0;
    const next = current + 1;
    // Cap at a reasonable maximum so the value can't run away.
    localStorage.setItem(VISIT_COUNT_KEY, String(Math.min(next, 999)));
    return next;
  } catch {
    return 1; // treat private-mode / blocked storage as a fresh first visit
  }
}

/** Schedule the banner show after `delayMs`. Re-checks the guards at
 *  fire time so a user who navigates / installs / dismisses during the
 *  wait still gets the right behavior. */
function scheduleBanner(html, delayMs) {
  setTimeout(() => {
    if (isStandalone())      return; // installed while we were waiting
    if (!isMobileViewport()) return; // resized to desktop
    if (recentlyDismissed()) return; // dismissed via in-app link
    if (!bannerEl || bannerEl.classList.contains("hidden")) {
      showBanner(html);
    }
  }, delayMs);
}

function hideBanner() {
  if (bannerEl) bannerEl.classList.add("hidden");
}

function showBanner(html) {
  if (!bannerEl) {
    bannerEl = document.createElement("div");
    bannerEl.id = "install-banner";
    bannerEl.className = "install-banner";
    bannerEl.setAttribute("role", "dialog");
    bannerEl.setAttribute("aria-label", "Install StudyVET");
    document.body.appendChild(bannerEl);
  }
  bannerEl.innerHTML = html;
  bannerEl.classList.remove("hidden");
  wireBanner();
}

function wireBanner() {
  if (!bannerEl) return;
  const dismiss = bannerEl.querySelector('[data-act="dismiss"]');
  if (dismiss) {
    dismiss.addEventListener("click", () => {
      markDismissed();
      hideBanner();
    });
  }
  const install = bannerEl.querySelector('[data-act="install"]');
  if (install) {
    install.addEventListener("click", async () => {
      if (!deferredPrompt) { hideBanner(); return; }
      install.disabled = true;
      try {
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        if (outcome !== "accepted") markDismissed();
      } catch (err) {
        console.warn("[install] prompt failed:", err);
      } finally {
        deferredPrompt = null;
        hideBanner();
      }
    });
  }
}

function androidBannerHtml() {
  return `
    <div class="install-banner-inner">
      <div class="install-banner-icon" aria-hidden="true">📲</div>
      <div class="install-banner-text">
        <strong>Install StudyVET</strong>
        <span class="install-banner-sub muted">Home-screen access, works offline.</span>
      </div>
      <div class="install-banner-actions">
        <button class="btn btn-ghost" data-act="dismiss">Later</button>
        <button class="btn btn-primary" data-act="install">Install</button>
      </div>
    </div>`;
}

function iosBannerHtml() {
  return `
    <div class="install-banner-inner">
      <div class="install-banner-icon" aria-hidden="true">📲</div>
      <div class="install-banner-text">
        <strong>Add StudyVET to Home Screen</strong>
        <span class="install-banner-sub muted">
          Tap <span class="ios-share-glyph" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="14" height="14" focusable="false">
              <path fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                    d="M12 3v12M8 7l4-4 4 4M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7"/>
            </svg>
          </span> Share, then <strong>Add to Home Screen</strong>.
        </span>
      </div>
      <div class="install-banner-actions">
        <button class="btn btn-ghost" data-act="dismiss">Got it</button>
      </div>
    </div>`;
}

/* Decide whether to actually surface the banner now. The
   beforeinstallprompt + appinstalled listeners are registered at module-load
   (above) so they never miss the event; this function gates the *display*. */
export function initInstallPrompt() {
  initialised = true;
  visitsThisInit = bumpVisitCount();

  // Diagnostic — open DevTools console on mobile (or via remote debugging) to
  // see exactly which guard tripped. Remove later once stable.
  const why = (msg) => console.info("[install] not showing:", msg);

  if (isStandalone())       { why("already standalone (installed)"); return; }
  if (!isMobileViewport())  { why("desktop viewport (" + window.innerWidth + "px)"); return; }
  if (recentlyDismissed())  { why("within 1-day cooldown"); return; }
  if (visitsThisInit < MIN_VISITS_BEFORE_SHOW) {
    why(`first visit (count=${visitsThisInit}) — banner deferred until next visit`);
    return;
  }

  // If beforeinstallprompt already fired before this ran, schedule the banner
  // for 30s from now (matches the listener-path delay) so cold-load timing
  // doesn't cause a near-instant pop on slower devices that fire the event
  // late.
  if (pendingShow && deferredPrompt) {
    pendingShow = false;
    scheduleBanner(androidBannerHtml(), INSTALL_DELAY_MS);
    return;
  }

  // iOS Safari path — no install event, so we show a manual tip after the
  // same 30s settle time.
  if (isIosSafari()) {
    scheduleBanner(iosBannerHtml(), INSTALL_DELAY_MS);
    return;
  }

  // Chrome on Android: if we're here, the event hasn't fired yet. It will
  // arrive via the module-level listener and trigger the banner then (with
  // the 30s delay applied there too). If it never arrives, the site doesn't
  // meet Chrome's install criteria — see chrome://flags or Lighthouse > PWA
  // for diagnostics.
  console.info("[install] waiting for beforeinstallprompt (Chrome PWA criteria)…");
}

/* Dev/admin helper — call from console to clear the cooldown AND the
   first-visit gate so the banner can re-trigger after a reload.
   Exposed via window for convenience. */
export function resetInstallCooldown() {
  try { localStorage.removeItem(DISMISS_KEY); } catch {}
  try { localStorage.removeItem(VISIT_COUNT_KEY); } catch {}
  console.info("[install] cooldown + visit count cleared, reload to re-evaluate");
}
if (typeof window !== "undefined") {
  window.svResetInstallPrompt = resetInstallCooldown;
}

/* ─── Public API for in-app install link ──────────────────────────────────
   Lets the home page (or any view) render its own "Install app" link that
   bypasses the cooldown. The link only renders if isInstallable() returns
   true; the click handler calls triggerInstall() which routes to either
   the native Chrome prompt or iOS instructions. */

/** Should an "Install app" call-to-action be shown right now? */
export function isInstallable() {
  if (isStandalone()) return false;          // already installed
  if (deferredPrompt)  return true;          // Chrome / Edge / Android
  if (isIosSafari())   return true;          // iOS Safari (we'll show instructions)
  return false;                              // unsupported browser
}

/** Trigger the install flow. Returns a string status:
 *    "accepted"        — user installed
 *    "dismissed"       — user said no (cooldown updated)
 *    "ios-instructions" — iOS Safari, caller should show share-sheet hint
 *    "already-installed"
 *    "unsupported"
 *    "error"
 */
export async function triggerInstall() {
  if (isStandalone()) return "already-installed";

  if (deferredPrompt) {
    try {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      const status = outcome === "accepted" ? "accepted" : "dismissed";
      if (status === "dismissed") markDismissed();
      deferredPrompt = null;
      hideBanner();
      return status;
    } catch (err) {
      console.warn("[install] triggerInstall failed:", err);
      return "error";
    }
  }

  if (isIosSafari()) return "ios-instructions";
  return "unsupported";
}
